import {memo, useCallback, useEffect, useRef, useState, type ChangeEvent} from "react";
import {createPortal} from "react-dom";
import {useLocation, useSearch} from "wouter";
import {useMutation, useQuery} from "@tanstack/react-query";
import {CircleX, FileText, Link2, Maximize2, Minimize2, MoreVertical, Save, Tag, Trash2, X} from "lucide-react";
import DOMPurify from "dompurify";
import {marked} from "marked";
import type {MastermindSpace, Tag as TagType} from "@shared/schema";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {Badge} from "@/components/ui/badge";
import {TabsContent} from "@/components/ui/tabs";
import {RichTextEditor} from "./RichTextEditor";
import {useToast} from "@/hooks/use-toast";
import {useDebounce} from "@/hooks/use-debounce";
import {useDebouncedDocumentTitleSuggestion} from "@/hooks/useDebouncedDocumentTitleSuggestion";
import {useTags} from "@/hooks/useTags";
import {resolveTagNamesToIds} from "@/lib/resolveTagNamesToIds";
import {queryClient} from "@/lib/queryClient";
import {
    ensureKnowledgeBaseItem,
    knowledgeBaseItemQueryKey,
    patchKnowledgeBaseItemInCaches,
} from "@/queries/knowledgeBaseDocuments";
import {knowledgeBaseService} from "@/services/knowledgeBaseService";
import {sourcesService} from "@/services/sourcesService";
import {mastermindSpacesService} from "@/services/mastermindSpacesService";
import {userProfileService} from "@/services/userProfileService";
import {useUserProfile} from "@/contexts/UserProfileContext";
import {useMastermind} from "@/contexts/MastermindContext";
import {useLibrary} from "@/contexts/LibraryContext";
import {useAuth} from "@/contexts/AuthContext";
import {useWorkspace} from "@/contexts/WorkspaceContext";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {documentTypes} from "@/components/rightSidebarDocumentTypes";
import {ThreadLinkingDialog} from "@/components/ThreadLinkingDialog";
import {ClipSourceLinkFeedback} from "@/components/ClipSourceLinkFeedback";
import {activeChatContext$} from "@/state/activeChatContext";
import {chatClipSource$} from "@/state/chatClipSource";
import {clipLinkFeedback$} from "@/state/clipLinkFeedback";
import {
    peekSessionFootnoteRef,
    rememberFootnoteRefForSource,
    resetDocumentClipFootnoteSession,
} from "@/state/documentClipFootnoteSession";
import {buildClipFootnoteParts} from "@/utils/clipSourceFootnote";

export type AutoSaveStatus = "idle" | "saving" | "saved";

function parseChatUrlSearch(search: string): {spaceId: string | null; topicId: string | null} {
    const q = search?.startsWith("?") ? search.slice(1) : search || "";
    const params = new URLSearchParams(q);
    return {
        spaceId: params.get("spaceId"),
        topicId: params.get("topicId"),
    };
}

function pickFirstNonEmpty(...parts: Array<string | null | undefined>): string | null {
    for (const p of parts) {
        const t = typeof p === "string" ? p.trim() : "";
        if (t) return t;
    }
    return null;
}

/** Resolve space/topic for creates on /chat: form → Legend activeChatContext → Mastermind → URL. */
export function getEffectiveCreateSpaceTopic(params: {
    formSpaceId: string;
    formTopicId: string;
    mastermindSpaceId: string | null;
    mastermindTopicId: string | null;
    chatSearch: string;
    chatPath: string;
}): {spaceId: string | null; topicId: string | null} {
    const legend = activeChatContext$.peek();
    const onChatRoute = params.chatPath === "/chat" || params.chatPath.startsWith("/chat/");
    const url = onChatRoute ? parseChatUrlSearch(params.chatSearch) : {spaceId: null as string | null, topicId: null as string | null};

    const spaceId = pickFirstNonEmpty(
        params.formSpaceId,
        legend.spaceId ?? undefined,
        params.mastermindSpaceId ?? undefined,
        url.spaceId ?? undefined
    );
    const topicId = pickFirstNonEmpty(
        params.formTopicId,
        legend.topicId ?? undefined,
        params.mastermindTopicId ?? undefined,
        url.topicId ?? undefined
    );

    return {spaceId, topicId};
}

const isHtml = (str: string): boolean => {
    return /<[a-z][\s\S]*>/i.test(str);
};

// Fix orphan list items by wrapping them in <ul> tags using DOM parsing
const wrapOrphanListItems = (html: string): string => {
    // Use DOMParser to properly analyze and fix the structure
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
    const container = doc.body.firstChild as HTMLElement;

    if (!container) return html;

    // Find all <li> elements that don't have a proper parent (ul/ol)
    const allListItems = container.querySelectorAll("li");
    const orphanLis: HTMLLIElement[] = [];

    allListItems.forEach((li) => {
        const parent = li.parentElement;
        if (parent && parent.tagName !== "UL" && parent.tagName !== "OL") {
            orphanLis.push(li as HTMLLIElement);
        }
    });

    if (orphanLis.length === 0) return html;

    // Helper to get next non-whitespace sibling
    const getNextElementSibling = (node: Node): Element | null => {
        let sibling = node.nextSibling;
        while (sibling) {
            // Skip whitespace-only text nodes
            if (sibling.nodeType === Node.TEXT_NODE && sibling.textContent?.trim() === "") {
                sibling = sibling.nextSibling;
                continue;
            }
            if (sibling.nodeType === Node.ELEMENT_NODE) {
                return sibling as Element;
            }
            break;
        }
        return null;
    };

    // Group consecutive orphan <li> elements and wrap them
    let currentGroup: HTMLLIElement[] = [];
    let lastParent: HTMLElement | null = null;

    const wrapGroup = () => {
        if (currentGroup.length === 0 || !lastParent) return;

        const ul = doc.createElement("ul");
        const firstLi = currentGroup[0];

        // Find the node to insert before (the first node after the last li in the group)
        let nodeAfterGroup: Node | null = currentGroup[currentGroup.length - 1].nextSibling;
        // Skip whitespace nodes
        while (nodeAfterGroup && nodeAfterGroup.nodeType === Node.TEXT_NODE && nodeAfterGroup.textContent?.trim() === "") {
            nodeAfterGroup = nodeAfterGroup.nextSibling;
        }

        // Insert the ul before the first li
        lastParent.insertBefore(ul, firstLi);

        // Move all lis into the ul
        currentGroup.forEach((li) => ul.appendChild(li));

        currentGroup = [];
    };

    orphanLis.forEach((li, index) => {
        const parent = li.parentElement;

        // Check if this li is consecutive with the previous one (same parent and adjacent)
        let isConsecutive = false;
        if (lastParent === parent && currentGroup.length > 0) {
            const lastLi = currentGroup[currentGroup.length - 1];
            const nextEl = getNextElementSibling(lastLi);
            isConsecutive = nextEl === li;
        }

        if (!isConsecutive && currentGroup.length > 0) {
            wrapGroup();
        }

        currentGroup.push(li);
        lastParent = parent;

        // Wrap the last group
        if (index === orphanLis.length - 1) {
            wrapGroup();
        }
    });

    return container.innerHTML;
};

// DOMPurify config - only allow necessary attributes (no style for security)
const purifyConfig = {
    ALLOWED_TAGS: [
        "p",
        "br",
        "strong",
        "b",
        "em",
        "i",
        "u",
        "a",
        "ul",
        "ol",
        "li",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "blockquote",
        "code",
        "pre",
        "hr",
        "span",
        "div",
        "img",
    ],
    ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "class"],
    ALLOW_DATA_ATTR: false,
};

const convertToHtml = (content: string): string => {
    if (!content) return "";

    if (isHtml(content)) {
        // Fix orphan list items before sanitizing
        const fixedHtml = wrapOrphanListItems(content);
        return DOMPurify.sanitize(fixedHtml, purifyConfig);
    }

    try {
        const html = marked.parse(content, {async: false}) as string;
        return DOMPurify.sanitize(html, purifyConfig);
    } catch (error) {
        const escaped = content
            .split("\n\n")
            .map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`)
            .join("");
        return DOMPurify.sanitize(escaped, purifyConfig);
    }
};

interface DocumentCreatorProps {
    onActivateCreateTab?: () => void;
    onAutoSaveStatusChange?: (status: AutoSaveStatus) => void;
    onEditingItemIdChange?: (editingItemId: string | null) => void;
    onOpenDocumentHandlerChange?: (handler: (doc: any) => Promise<void>) => void;
    /** When set, space select and fullscreen button are portaled into this element (e.g. tab bar). */
    tabBarActionsContainerId?: string;
}

export const DocumentCreator = memo(function DocumentCreator({
                                                                 onActivateCreateTab,
                                                                 onAutoSaveStatusChange,
                                                                 onEditingItemIdChange,
                                                                 onOpenDocumentHandlerChange,
                                                                 tabBarActionsContainerId,
                                                             }: DocumentCreatorProps) {
    const {toast} = useToast();
    const {user} = useAuth();
    const {currentWorkspaceId} = useWorkspace();
    const {currentSpaceId, currentTopicId, registerSidebarClipHandler, registerCancelEditHandler} = useMastermind();
    const chatSearch = useSearch();
    const [chatPath] = useLocation();
    const {
        selectedDocumentId,
        setSelectedDocumentId,
        selectedReferenceId,
        setSelectedReferenceId,
        documentEditSpaceContextId,
        setDocumentEditSpaceContextId,
    } = useLibrary();
    const [formData, setFormData] = useState({
        title: "",
        type: "note",
        content: "",
        spaceId: "" as string,
        topicId: "" as string,
        tagIds: [] as string[],
        syncToGoogleDocs: false,
        url: "" as string,
    });
    const formDataRef = useRef(formData);
    formDataRef.current = formData;
    const [editingItemId, setEditingItemId] = useState<string | null>(null);
    const [editingItemKind, setEditingItemKind] = useState<"note" | "reference" | null>(null);
    const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>("idle");
    const [isContentFullscreen, setIsContentFullscreen] = useState(false);
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const editorRef = useRef<any>(null);
    const lastCursorPositionRef = useRef<number | null>(null);

    // Debounced form data for auto-save
    const debouncedFormData = useDebounce(formData, 2000);

    // Track last saved state for all fields (not just content)
    const lastSavedStateRef = useRef<{
        title: string;
        type: string;
        content: string;
        spaceId: string;
        topicId: string;
        tagIds: string[];
        url?: string;
    } | null>(null);

    // Ref to track if we're currently loading a document to prevent concurrent loads
    const isLoadingDocumentRef = useRef(false);

    /** Incremented when opening a doc from the library to cancel in-flight auto-creates. */
    const autoCreateGenerationRef = useRef(0);
    /** Incremented per load attempt so stale load completions are ignored. */
    const loadGenerationRef = useRef(0);

    // Ref to track if we've already attempted to restore persisted document (only once on mount)
    const hasAttemptedRestoreRef = useRef(false);

    // Ref to track if user has manually cancelled editing (to prevent re-restoration)
    const hasCancelledEditRef = useRef(false);

    // Ref to prevent multiple concurrent auto-creates when creating a new document
    const isAutoCreatingRef = useRef(false);

    // Ref to prevent auto-save when manual save is in progress
    const isManualSavingRef = useRef(false);

    // Ref to track current editingItemId to avoid stale closures
    const editingItemIdRef = useRef<string | null>(null);

    // Ref to store spaceIds and topicIds arrays from metadata for auto-save preservation
    const editingItemMetadataRef = useRef<{
        spaceIds: string[];
        topicIds: string[];
    } | null>(null);

    /** Stable space context for shared RPC; survives selectedDocumentId clear after load. */
    const editingSpaceContextIdRef = useRef<string | null>(null);
    /** When true, include spaceIds/topicIds in save payloads (user changed links). */
    const linksDirtyRef = useRef(false);
    /** Bumped on manual save to cancel in-flight debounced auto-saves. */
    const autoSaveGenerationRef = useRef(0);

    const resetTitleSuggestionRef = useRef<() => void>(() => {});

    /** True after the user changes the form (not on open/load). Auto-save is gated on this. */
    const userHasEditedSinceLoadRef = useRef(false);
    /** While true, TipTap initial HTML sync must not count as a user edit. */
    const isInitialEditorSyncRef = useRef(false);
    const initialLoadBaselineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearInitialLoadBaselineTimer = useCallback(() => {
        if (initialLoadBaselineTimerRef.current) {
            clearTimeout(initialLoadBaselineTimerRef.current);
            initialLoadBaselineTimerRef.current = null;
        }
    }, []);

    /**
     * After loading a document/reference, TipTap HTML differs from convertToHtml(); re-baseline
     * lastSavedStateRef once the editor has settled so we do not auto-save "fake" diffs.
     * Also resets the user-edited flag until the user actually edits.
     */
    const scheduleEditorBaselineAfterLoad = useCallback(() => {
        clearInitialLoadBaselineTimer();
        isInitialEditorSyncRef.current = true;
        userHasEditedSinceLoadRef.current = false;
        initialLoadBaselineTimerRef.current = setTimeout(() => {
            initialLoadBaselineTimerRef.current = null;
            isInitialEditorSyncRef.current = false;
            const id = editingItemIdRef.current;
            const last = lastSavedStateRef.current;
            const fd = formDataRef.current;
            if (id && last) {
                lastSavedStateRef.current = {
                    ...last,
                    title: fd.title,
                    type: fd.type,
                    content: fd.content,
                    spaceId: fd.spaceId,
                    topicId: fd.topicId,
                    tagIds: [...fd.tagIds],
                    ...("url" in last ? {url: fd.url} : {}),
                };
            }
        }, 500);
    }, [clearInitialLoadBaselineTimer]);

    useEffect(() => {
        return () => clearInitialLoadBaselineTimer();
    }, [clearInitialLoadBaselineTimer]);

    const persistEditingSpaceContext = useCallback(
        (linkedSpaceIds: string[]) => {
            const linked = linkedSpaceIds.map((id) => String(id));
            const explicit = documentEditSpaceContextId?.trim();
            const fromMeta = linked[0];
            let ctx: string | null = explicit || fromMeta || null;
            if (!ctx && currentSpaceId && linked.includes(currentSpaceId)) {
                ctx = currentSpaceId;
            }
            editingSpaceContextIdRef.current = ctx;
            linksDirtyRef.current = false;
        },
        [documentEditSpaceContextId, currentSpaceId]
    );

    /** Third arg to updateKnowledgeBaseItem: required for editor updates to another user's doc (shared RPC). */
    const getSpaceContextForSharedEdit = useCallback((): string | undefined => {
        const linkedIds = editingItemMetadataRef.current?.spaceIds ?? [];
        const fromExplicit =
            documentEditSpaceContextId?.trim() || editingSpaceContextIdRef.current?.trim() || undefined;
        const fromMeta = linkedIds[0];
        const fromForm = formData.spaceId?.trim();
        if (fromExplicit) return fromExplicit;
        if (fromMeta) return fromMeta;
        if (fromForm) return fromForm;
        if (currentSpaceId && linkedIds.includes(currentSpaceId)) return currentSpaceId;
        return currentSpaceId || undefined;
    }, [documentEditSpaceContextId, currentSpaceId, formData.spaceId]);

    const buildDocumentUpdateData = useCallback(
        (fields: {title: string; type: string; content: string; tagIds: string[]}) => {
            const updateData: {
                title: string;
                type: string;
                content: string;
                tagIds: string[];
                spaceIds?: string[];
                topicIds?: string[];
            } = {...fields};
            if (linksDirtyRef.current && editingItemMetadataRef.current) {
                updateData.spaceIds = editingItemMetadataRef.current.spaceIds;
                updateData.topicIds = editingItemMetadataRef.current.topicIds;
            }
            return updateData;
        },
        []
    );

    const resolveEffectiveCreateIds = useCallback(
        (formSpaceId: string, formTopicId: string) =>
            getEffectiveCreateSpaceTopic({
                formSpaceId,
                formTopicId,
                mastermindSpaceId: currentSpaceId,
                mastermindTopicId: currentTopicId,
                chatSearch,
                chatPath,
            }),
        [currentSpaceId, currentTopicId, chatSearch, chatPath]
    );

    const runPostCreateDocumentLink = useCallback(
        async (documentId: string, eff: {spaceId: string | null; topicId: string | null}) => {
            if (!eff.spaceId) return;
            try {
                await knowledgeBaseService.linkDocumentToSpaceTopic(
                    documentId,
                    eff.spaceId,
                    eff.topicId ?? undefined
                );
                await queryClient.invalidateQueries({queryKey: ["/api/knowledgebase"], exact: false});
                await queryClient.invalidateQueries({queryKey: ["knowledgebase-summary"]});
                await queryClient.invalidateQueries({queryKey: ["/api/knowledgebase-members", eff.spaceId]});
                await queryClient.invalidateQueries({queryKey: ["/api/spaces", eff.spaceId, "topics"]});
                await queryClient.invalidateQueries({queryKey: ["/api/topics"]});
                await queryClient.invalidateQueries({queryKey: knowledgeBaseItemQueryKey(documentId)});
            } catch (error: unknown) {
                const message = error instanceof Error ? error.message : "Failed to link document to space or topic";
                toast({
                    title: "Link failed",
                    description: message,
                    variant: "destructive",
                });
            }
        },
        [toast]
    );

    // Fetch user profile for sidebar persistence
    const {profile: userProfile, isLoading: profileLoading} = useUserProfile();
    const profileLoaded = !profileLoading;

    // Persist open document state to Supabase (defined early so it can be used in resetForm)
    const persistOpenDocMutation = useMutation({
        mutationFn: async (documentId: string | null) => {
            try {
                const currentPrefs = (userProfile?.preferences as any) || {};
                await userProfileService.updateProfile({
                    preferences: {
                        ...currentPrefs,
                        sidebarOpenDocumentId: documentId,
                    },
                });
            } catch (error) {
                console.error("Failed to persist document state:", error);
            }
        },
    });

    const resetForm = useCallback(
        (clearPersistence = true) => {
            // Cancel any in-flight auto-create and block autosave until the user edits again
            autoCreateGenerationRef.current += 1;
            isAutoCreatingRef.current = false;
            hasCancelledEditRef.current = true;

            // TipTap will emit onChange when content clears — treat as programmatic, not a user edit
            isInitialEditorSyncRef.current = true;
            userHasEditedSinceLoadRef.current = false;
            clearInitialLoadBaselineTimer();

            setFormData({
                title: "",
                type: "note",
                content: "",
                spaceId: currentSpaceId || "",
                topicId: "",
                tagIds: [],
                syncToGoogleDocs: false,
                url: "",
            });
            setEditingItemId(null);
            editingItemIdRef.current = null;
            setEditingItemKind(null);
            lastSavedStateRef.current = null;
            editingItemMetadataRef.current = null;
            editingSpaceContextIdRef.current = null;
            linksDirtyRef.current = false;
            autoSaveGenerationRef.current += 1;
            setDocumentEditSpaceContextId(null);
            setAutoSaveStatus("idle");
            isManualSavingRef.current = false;

            initialLoadBaselineTimerRef.current = setTimeout(() => {
                initialLoadBaselineTimerRef.current = null;
                isInitialEditorSyncRef.current = false;
            }, 500);

            resetTitleSuggestionRef.current();

            // Clear persisted document state
            if (clearPersistence) {
                persistOpenDocMutation.mutate(null);
            }
        },
        [currentSpaceId, persistOpenDocMutation, clearInitialLoadBaselineTimer, setDocumentEditSpaceContextId]
    );

    const markUserEdited = useCallback(() => {
        hasCancelledEditRef.current = false;
        userHasEditedSinceLoadRef.current = true;
    }, []);

    const handleTagToggle = useCallback((tagId: string) => {
        markUserEdited();
        setFormData((prev) => ({
            ...prev,
            tagIds: prev.tagIds.includes(tagId) ? prev.tagIds.filter((id) => id !== tagId) : [...prev.tagIds, tagId],
        }));
    }, []);

    const suggestTitleEnabled = !editingItemId && editingItemKind !== "reference";

    const {syncTitleRef, onUserEditedTitle, resetSuggestionState} = useDebouncedDocumentTitleSuggestion({
        contentHtml: formData.content,
        setTitle: (value) =>
            setFormData((prev) => ({
                ...prev,
                title: typeof value === "function" ? (value as (t: string) => string)(prev.title) : value,
            })),
        enabled: suggestTitleEnabled,
        debounceMs: 1000,
    });
    resetTitleSuggestionRef.current = resetSuggestionState;

    useEffect(() => {
        syncTitleRef(formData.title);
    }, [formData.title, syncTitleRef]);

    const handleContentChange = useCallback((content: string) => {
        if (!isInitialEditorSyncRef.current) {
            markUserEdited();
        }
        setFormData((prev) => ({...prev, content}));
    }, [markUserEdited]);

    const handleTitleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const {value} = event.target;
            onUserEditedTitle();
            markUserEdited();
            setFormData((prev) => ({...prev, title: value}));
        },
        [onUserEditedTitle, markUserEdited]
    );

    const handleTypeChange = useCallback((value: string) => {
        markUserEdited();
        setFormData((prev) => ({...prev, type: value}));
    }, [markUserEdited]);

    const handleUrlChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        markUserEdited();
        setFormData((prev) => ({...prev, url: event.target.value}));
    }, [markUserEdited]);

    // Keep editingItemIdRef in sync with editingItemId state
    useEffect(() => {
        editingItemIdRef.current = editingItemId;
    }, [editingItemId]);

    // Watch for selectedDocumentId from LibraryContext (when clicking documents in Library)
    useEffect(() => {
        // Prevent concurrent loads and avoid loading if already editing this document
        if (selectedDocumentId && selectedDocumentId !== editingItemId && !isLoadingDocumentRef.current) {
            autoCreateGenerationRef.current += 1;
            isAutoCreatingRef.current = false;
            userHasEditedSinceLoadRef.current = false;
            isInitialEditorSyncRef.current = true;
            clearInitialLoadBaselineTimer();

            isLoadingDocumentRef.current = true;
            const loadGeneration = ++loadGenerationRef.current;

            const loadDocument = async (retryCount = 0) => {
                if (loadGenerationRef.current !== loadGeneration) {
                    isLoadingDocumentRef.current = false;
                    return;
                }
                try {
                    // Full document via canonical React Query cache (deduped across app).
                    let doc =
                        (await ensureKnowledgeBaseItem(queryClient, selectedDocumentId)) ??
                        queryClient.getQueryData(knowledgeBaseItemQueryKey(selectedDocumentId));

                    if (doc) {
                        if (loadGenerationRef.current !== loadGeneration) {
                            isLoadingDocumentRef.current = false;
                            return;
                        }

                        const htmlContent = convertToHtml(doc.content || "");

                        // Extract spaceIds and topicIds from metadata (support both array and single value)
                        const metadata = doc.metadata || {};
                        const parsedMetadata = typeof metadata === 'string' ? (() => {
                            try {
                                return JSON.parse(metadata);
                            } catch {
                                return {};
                            }
                        })() : metadata;

                        // Get spaceIds array (new format) or fallback to single spaceId
                        const spaceIds = Array.isArray(parsedMetadata.spaceIds)
                            ? parsedMetadata.spaceIds
                            : (parsedMetadata.spaceId ? [parsedMetadata.spaceId] : (doc.spaceId ? [doc.spaceId] : []));

                        // Get topicIds array (new format) or fallback to single topicId
                        const topicIds = Array.isArray(parsedMetadata.topicIds)
                            ? parsedMetadata.topicIds
                            : (parsedMetadata.topicId ? [parsedMetadata.topicId] : (doc.topicId ? [doc.topicId] : []));

                        // Get tagIds from metadata
                        const tagIds = Array.isArray(parsedMetadata.tagIds)
                            ? parsedMetadata.tagIds
                            : (doc.tagIds || []);

                        setEditingItemKind("note");
                        editingItemIdRef.current = doc.id;
                        setEditingItemId(doc.id);
                        setFormData({
                            title: doc.title || "",
                            type: doc.type || "note",
                            content: htmlContent,
                            spaceId: spaceIds.length > 0 ? spaceIds[0] : "",
                            topicId: topicIds.length > 0 ? topicIds[0] : "",
                            tagIds: tagIds,
                            syncToGoogleDocs: false,
                            url: "",
                        });

                        // Store spaceIds and topicIds arrays in a ref for auto-save preservation
                        editingItemMetadataRef.current = {
                            spaceIds: spaceIds.map((id: any) => String(id)),
                            topicIds: topicIds.map((id: any) => String(id)),
                        };
                        persistEditingSpaceContext(spaceIds.map((id: any) => String(id)));

                        // Store normalized HTML for comparison
                        lastSavedStateRef.current = {
                            title: doc.title || "",
                            type: doc.type || "note",
                            content: htmlContent,
                            spaceId: spaceIds.length > 0 ? spaceIds[0] : "",
                            topicId: topicIds.length > 0 ? topicIds[0] : "",
                            tagIds: tagIds,
                        };
                        scheduleEditorBaselineAfterLoad();
                        resetTitleSuggestionRef.current();
                        onActivateCreateTab?.();
                        // Persist this document as the open document
                        persistOpenDocMutation.mutate(doc.id);
                        // Reset cancelled flag when user manually selects a document
                        hasCancelledEditRef.current = false;

                        // Only clear the selection after successfully loading to allow re-selecting the same document
                        // Use setTimeout to ensure state updates are complete first
                        setTimeout(() => {
                            setSelectedDocumentId(null);
                            isLoadingDocumentRef.current = false;
                        }, 0);
                    } else {
                        // Document not found - retry with exponential backoff (max 3 retries)
                        if (retryCount < 3) {
                            const delay = Math.min(1000 * Math.pow(2, retryCount), 3000); // 1s, 2s, 3s
                            console.log(`Document not found, retrying in ${delay}ms... (attempt ${retryCount + 1}/3)`);
                            setTimeout(() => {
                                loadDocument(retryCount + 1);
                            }, delay);
                        } else {
                            console.error("Failed to load document after 3 retries:", selectedDocumentId);
                            toast({
                                title: "Document Not Found",
                                description: "The document could not be loaded. Please try again.",
                                variant: "destructive",
                            });
                            setSelectedDocumentId(null);
                            isLoadingDocumentRef.current = false;
                        }
                    }
                } catch (error) {
                    console.error("Failed to load document:", error);
                    // Retry on error with exponential backoff (max 3 retries)
                    if (retryCount < 3) {
                        const delay = Math.min(1000 * Math.pow(2, retryCount), 3000); // 1s, 2s, 3s
                        console.log(`Error loading document, retrying in ${delay}ms... (attempt ${retryCount + 1}/3)`);
                        setTimeout(() => {
                            loadDocument(retryCount + 1);
                        }, delay);
                    } else {
                        toast({
                            title: "Failed to Load Document",
                            description: "There was an error loading the document. Please try again.",
                            variant: "destructive",
                        });
                        setSelectedDocumentId(null);
                        isLoadingDocumentRef.current = false;
                    }
                }
            };
            loadDocument();
        }
        // Intentionally narrow: avoid re-fetching the document when unrelated context (toast, user, etc.) changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDocumentId, editingItemId, scheduleEditorBaselineAfterLoad, persistEditingSpaceContext]);

    // Watch for selectedReferenceId from LibraryContext (when clicking Ref in Library) — open in same panel for editing
    useEffect(() => {
        if (!selectedReferenceId || selectedReferenceId === editingItemId) return;
        if (isLoadingDocumentRef.current) return;
        isLoadingDocumentRef.current = true;
        const loadReference = async () => {
            try {
                const source = await sourcesService.getSource(selectedReferenceId);
                if (source) {
                    const htmlContent = convertToHtml(source.content || "");
                    setEditingItemKind("reference");
                    editingItemIdRef.current = source.id;
                    setEditingItemId(source.id);
                    setFormData({
                        title: source.title || "",
                        type: source.type || "url",
                        content: htmlContent,
                        spaceId: "",
                        topicId: "",
                        tagIds: [],
                        syncToGoogleDocs: false,
                        url: source.url || "",
                    });
                    lastSavedStateRef.current = {
                        title: source.title || "",
                        type: source.type || "url",
                        content: htmlContent,
                        spaceId: "",
                        topicId: "",
                        tagIds: [],
                        url: source.url || "",
                    };
                    scheduleEditorBaselineAfterLoad();
                    resetTitleSuggestionRef.current();
                    hasCancelledEditRef.current = false;
                    onActivateCreateTab?.();
                } else {
                    toast({
                        title: "Reference Not Found",
                        description: "The reference could not be loaded.",
                        variant: "destructive",
                    });
                }
            } catch (err) {
                console.error("Failed to load reference:", err);
                toast({
                    title: "Failed to Load Reference",
                    description: "There was an error loading the reference.",
                    variant: "destructive",
                });
            } finally {
                setSelectedReferenceId(null);
                isLoadingDocumentRef.current = false;
            }
        };
        loadReference();
    }, [selectedReferenceId, editingItemId, onActivateCreateTab, toast, scheduleEditorBaselineAfterLoad]);

    // Restore persisted document on mount - DISABLED: Documents should only be loaded when manually selected
    // This was causing automatic document selection on login, which is not desired behavior
    // useEffect(() => {
    //   if (!profileLoaded || !userProfile?.preferences) return;
    //   if (hasAttemptedRestoreRef.current) return; // Only attempt once
    //   if (hasCancelledEditRef.current) return; // Don't restore if user cancelled

    //   const prefs = userProfile.preferences as any;
    //   if (prefs.sidebarOpenDocumentId && !editingItemId && !selectedDocumentId) {
    //     hasAttemptedRestoreRef.current = true;
    //     const loadPersistedDoc = async () => {
    //       try {
    //         const doc = await knowledgeBaseService.getKnowledgeBaseItem(prefs.sidebarOpenDocumentId);
    //         if (doc) {
    //           const htmlContent = convertToHtml(doc.content || "");
    //           setEditingItemId(doc.id);
    //           setFormData({
    //             title: doc.title || "",
    //             type: doc.type || "note",
    //             content: htmlContent,
    //             spaceId: doc.spaceId || "",
    //             topicId: doc.topicId || "",
    //             tagIds: doc.tagIds || [],
    //             syncToGoogleDocs: false,
    //           });
    //           // Store normalized HTML for comparison
    //           lastSavedStateRef.current = {
    //             title: doc.title || "",
    //             type: doc.type || "note",
    //             content: htmlContent,
    //             spaceId: doc.spaceId || "",
    //             topicId: doc.topicId || "",
    //             tagIds: doc.tagIds || [],
    //           };
    //           onActivateCreateTab?.();
    //         }
    //       } catch (error) {
    //         console.error("Failed to load persisted document:", error);
    //       }
    //     };
    //     loadPersistedDoc();
    //   }
    // }, [profileLoaded, userProfile?.preferences, editingItemId, selectedDocumentId, onActivateCreateTab]);

    // Auto-save when form data changes (debounced)
    // - New document: auto-create when user has entered a title and stopped typing (notes only)
    // - Existing document/reference: update when any field changed
    useEffect(() => {
        // Skip auto-save if manual save is in progress
        if (isManualSavingRef.current) return;

        // Only auto-save after a real user edit (not on open / TipTap normalization)
        if (!userHasEditedSinceLoadRef.current) return;

        // After cancel edit, ignore stale debounced form data until the user edits again
        if (hasCancelledEditRef.current) return;

        // Do not create/update while an existing document is being opened from the library
        if (isLoadingDocumentRef.current) return;

        if (!debouncedFormData.title.trim()) return;
        const currentEditingId = editingItemId || editingItemIdRef.current;

        // New document: auto-create when user has added a title and stopped typing (notes only)
        if (!currentEditingId && editingItemKind !== "reference") {
            // User clicked a document in the list — wait for load, do not create a duplicate row
            if (selectedDocumentId) return;

            // Prevent multiple concurrent auto-creates
            if (isAutoCreatingRef.current) return;

            const createGeneration = ++autoCreateGenerationRef.current;
            isAutoCreatingRef.current = true;
            setAutoSaveStatus("saving");

            const eff = resolveEffectiveCreateIds(debouncedFormData.spaceId, debouncedFormData.topicId);
            const docData = {
                title: debouncedFormData.title,
                type: debouncedFormData.type,
                content: debouncedFormData.content,
                spaceId: eff.spaceId || null,
                topicId: eff.topicId || null,
                tagIds: debouncedFormData.tagIds.length > 0 ? debouncedFormData.tagIds : [],
            };

            knowledgeBaseService
                .createKnowledgeBaseItem(docData, currentWorkspaceId ?? null)
                .then(async (created) => {
                    if (autoCreateGenerationRef.current !== createGeneration) return;
                    if (selectedDocumentId) return;

                    // Set editing ID to switch to update mode
                    editingItemIdRef.current = created.id;
                    setEditingItemId(created.id);
                    setEditingItemKind("note");
                    // Store saved state to prevent duplicate saves
                    lastSavedStateRef.current = {
                        title: debouncedFormData.title,
                        type: debouncedFormData.type,
                        content: debouncedFormData.content,
                        spaceId: debouncedFormData.spaceId,
                        topicId: debouncedFormData.topicId,
                        tagIds: [...debouncedFormData.tagIds],
                    };
                    persistOpenDocMutation.mutate(created.id);
                    await runPostCreateDocumentLink(created.id, eff);
                    setAutoSaveStatus("saved");
                    queryClient.invalidateQueries({queryKey: ["/api/knowledgebase"]});
                    queryClient.invalidateQueries({queryKey: ["knowledgebase-summary"]});
                    setTimeout(() => setAutoSaveStatus("idle"), 2000);
                })
                .catch((error) => {
                    console.error("Auto-save (create) failed:", error);
                    setAutoSaveStatus("idle");
                })
                .finally(() => {
                    isAutoCreatingRef.current = false;
                });
            return;
        }

        // Existing document or reference: update when there are changes
        if (!currentEditingId || !lastSavedStateRef.current) return;

        const lastState = lastSavedStateRef.current;
        const hasChanges =
            debouncedFormData.title !== lastState.title ||
            debouncedFormData.type !== lastState.type ||
            debouncedFormData.content !== lastState.content ||
            debouncedFormData.spaceId !== lastState.spaceId ||
            debouncedFormData.topicId !== lastState.topicId ||
            JSON.stringify(debouncedFormData.tagIds) !== JSON.stringify(lastState.tagIds) ||
            (editingItemKind === "reference" && debouncedFormData.url !== (lastState.url ?? ""));
        if (!hasChanges) return;

        const autoSave = async () => {
            const saveGeneration = autoSaveGenerationRef.current;
            setAutoSaveStatus("saving");
            try {
                if (editingItemKind === "reference" && currentEditingId) {
                    await sourcesService.updateSource(currentEditingId, {
                        title: debouncedFormData.title,
                        type: debouncedFormData.type,
                        content: debouncedFormData.content,
                        url: debouncedFormData.url || undefined,
                    } as any);
                    lastSavedStateRef.current = {
                        ...lastState,
                        title: debouncedFormData.title,
                        type: debouncedFormData.type,
                        content: debouncedFormData.content,
                        url: debouncedFormData.url,
                    };
                    queryClient.invalidateQueries({queryKey: ["/api/sources"]});
                    queryClient.invalidateQueries({queryKey: ["source", currentEditingId]});
                } else if (currentEditingId) {
                    const updateData = buildDocumentUpdateData({
                        title: debouncedFormData.title,
                        type: debouncedFormData.type,
                        content: debouncedFormData.content,
                        tagIds: debouncedFormData.tagIds,
                    });

                    const updated = await knowledgeBaseService.updateKnowledgeBaseItem(
                        currentEditingId,
                        updateData,
                        getSpaceContextForSharedEdit()
                    );
                    if (saveGeneration !== autoSaveGenerationRef.current) return;
                    lastSavedStateRef.current = {
                        title: debouncedFormData.title,
                        type: debouncedFormData.type,
                        content: debouncedFormData.content,
                        spaceId: debouncedFormData.spaceId,
                        topicId: debouncedFormData.topicId,
                        tagIds: [...debouncedFormData.tagIds],
                    };
                    if (linksDirtyRef.current) {
                        linksDirtyRef.current = false;
                    }
                    if (updated) {
                        patchKnowledgeBaseItemInCaches(queryClient, currentEditingId, {
                            title: updated.title,
                            type: updated.type,
                            content: updated.content,
                            tagIds: updated.tagIds,
                            updatedAt: updated.updatedAt,
                        });
                    }
                }
                if (saveGeneration !== autoSaveGenerationRef.current) return;
                setAutoSaveStatus("saved");
                setTimeout(() => setAutoSaveStatus("idle"), 2000);
            } catch (error) {
                if (saveGeneration !== autoSaveGenerationRef.current) return;
                console.error("Auto-save failed:", error);
                setAutoSaveStatus("idle");
            }
        };

        autoSave();
    }, [
        debouncedFormData,
        editingItemId,
        editingItemKind,
        selectedDocumentId,
        persistOpenDocMutation,
        getSpaceContextForSharedEdit,
        buildDocumentUpdateData,
        resolveEffectiveCreateIds,
        runPostCreateDocumentLink,
        currentWorkspaceId,
    ]);

    // Update spaceId and topicId when current space/topic context changes (e.g. user is on a space or topic page)
    useEffect(() => {
        if (currentSpaceId && !editingItemId) {
            setFormData((prev) => ({...prev, spaceId: currentSpaceId, topicId: currentTopicId || ""}));
        }
    }, [currentSpaceId, currentTopicId, editingItemId]);

    // Track cursor position in the editor
    useEffect(() => {
        const editor = editorRef.current;
        if (!editor) return;

        const saveCursorPosition = () => {
            try {
                const {anchor} = editor.state.selection;
                if (anchor !== null && anchor !== undefined) {
                    lastCursorPositionRef.current = anchor;
                }
            } catch (error) {
                // Ignore errors if selection is not available
            }
        };

        const handleUpdate = () => {
            saveCursorPosition();
        };

        const handleSelectionUpdate = () => {
            saveCursorPosition();
        };

        editor.on("update", handleUpdate);
        editor.on("selectionUpdate", handleSelectionUpdate);

        return () => {
            editor.off("update", handleUpdate);
            editor.off("selectionUpdate", handleSelectionUpdate);
        };
    }, [editorRef]);

    useEffect(() => {
        resetDocumentClipFootnoteSession(editingItemId ?? "__draft__");
    }, [editingItemId]);

    // Register handler for clipping content from SelectionToolbar (e.g. YouTube "Send to Document Creator")
    useEffect(() => {
        const handleClip = (content: string, title?: string, documentType?: string) => {
            markUserEdited();
            const htmlContentBase = convertToHtml(content);
            const editor = editorRef.current;
            const clipSource = chatClipSource$.peek();
            const docKey = editingItemIdRef.current ?? "__draft__";

            let clipInnerHtml = htmlContentBase;
            let trailingFooterHtml: string | null = null;
            let linkedToSource = false;

            if (clipSource.sourceUrl && clipSource.sourceLabel) {
                const docHtml = editor?.getHTML() ?? formDataRef.current.content ?? "";
                const sessionRef = peekSessionFootnoteRef(docKey, clipSource.sourceUrl);
                const parts = buildClipFootnoteParts({
                    documentHtml: docHtml,
                    clipInnerHtml: htmlContentBase,
                    sourceUrl: clipSource.sourceUrl,
                    sourceLabel: clipSource.sourceLabel,
                    existingRefOverride: sessionRef,
                });
                clipInnerHtml = parts.mainInnerHtml;
                trailingFooterHtml = parts.trailingFooterHtml;
                linkedToSource = parts.didLink;
                if (clipSource.sourceUrl && parts.refNum != null) {
                    rememberFootnoteRefForSource(clipSource.sourceUrl, parts.refNum);
                }
            }

            const insertMain = `<hr/>${clipInnerHtml}`;

            // Set document title and/or type when provided (e.g. from YouTube Quick Import -> type "youtube")
            setFormData((prev) => ({
                ...prev,
                ...(title ? {title} : {}),
                ...(documentType ? {type: documentType} : {}),
            }));

            const showLinkFeedback = () => {
                if (!linkedToSource) return;
                clipLinkFeedback$.message.set("Linked to source");
                window.setTimeout(() => {
                    clipLinkFeedback$.message.set(null);
                }, 4000);
            };

            // Try to insert at saved cursor position if editor is available
            if (editor) {
                // Focus the editor first
                editor.chain().focus().run();

                // Small delay to ensure editor is focused before inserting
                setTimeout(() => {
                    const savedPosition = lastCursorPositionRef.current;
                    const currentPosition = editor.state.selection.anchor;
                    const docSize = editor.state.doc.content.size;

                    // Determine where to insert
                    let insertPosition: number;

                    if (savedPosition !== null && savedPosition !== undefined) {
                        // Validate saved position is within document bounds
                        if (savedPosition >= 0 && savedPosition <= docSize) {
                            insertPosition = savedPosition;
                        } else {
                            // Saved position is out of bounds, use current position or end
                            insertPosition = currentPosition > 0 ? currentPosition : docSize;
                        }
                    } else {
                        // No saved position - use current cursor position or end
                        insertPosition = currentPosition > 0 ? currentPosition : docSize;
                    }

                    try {
                        editor.chain().focus().setTextSelection(insertPosition).insertContent(insertMain).run();

                        if (trailingFooterHtml) {
                            const endPos = editor.state.doc.content.size;
                            editor.chain().focus().setTextSelection(endPos).insertContent(trailingFooterHtml).run();
                        }

                        const newPosition = insertPosition + clipInnerHtml.length + 5;
                        lastCursorPositionRef.current = newPosition;
                        showLinkFeedback();
                    } catch (error) {
                        // Fallback: insert at current cursor or append to end
                        console.warn("Failed to insert at specific position, using fallback:", error);
                        if (currentPosition > 0) {
                            editor.chain().focus().insertContent(insertMain).run();
                            if (trailingFooterHtml) {
                                const endPos = editor.state.doc.content.size;
                                editor.chain().focus().setTextSelection(endPos).insertContent(trailingFooterHtml).run();
                            }
                        } else {
                            const currentContent = editor.getHTML();
                            const merged = currentContent
                                ? `${currentContent}${insertMain}${trailingFooterHtml ?? ""}`
                                : `${clipInnerHtml}${trailingFooterHtml ?? ""}`;
                            editor.commands.setContent(merged);
                            editor.chain().focus().run();
                        }
                        showLinkFeedback();
                    }
                }, 10);
            } else {
                setFormData((prev) => ({
                    ...prev,
                    content: prev.content
                        ? `${prev.content}${insertMain}${trailingFooterHtml ?? ""}`
                        : `${clipInnerHtml}${trailingFooterHtml ?? ""}`,
                }));
                showLinkFeedback();
            }
            // Switch to create tab to show the clipped content
            onActivateCreateTab?.();
        };

        registerSidebarClipHandler(handleClip);
    }, [registerSidebarClipHandler, onActivateCreateTab, markUserEdited]);

    // Register cancel-edit handler so external callers (e.g. YouTube "Send to Document Creator") can close the current session first.
    // Use a ref so we don't re-register when resetForm identity changes (which would trigger context updates and an infinite loop).
    const resetFormRef = useRef(resetForm);
    resetFormRef.current = resetForm;
    useEffect(() => {
        registerCancelEditHandler(() => resetFormRef.current?.());
    }, [registerCancelEditHandler]);

    // Fetch spaces (workspace-scoped)
    const { data: spaces = [] } = useQuery<MastermindSpace[]>({
        queryKey: ["/api/spaces", currentWorkspaceId ?? null],
        queryFn: async () => {
            return await mastermindSpacesService.getMastermindSpaces(currentWorkspaceId ?? null);
        },
    });

    const {data: tags = []} = useTags();

    const createDocumentMutation = useMutation({
        mutationFn: async (data: typeof formData) => {
            const eff = resolveEffectiveCreateIds(data.spaceId, data.topicId);
            const docData = {
                title: data.title,
                type: data.type,
                content: data.content,
                spaceId: eff.spaceId || null,
                topicId: eff.topicId || null,
                tagIds: data.tagIds.length > 0 ? data.tagIds : [],
            };

            const created = await knowledgeBaseService.createKnowledgeBaseItem(docData, currentWorkspaceId ?? null);
            return {created, eff};
        },
        onSuccess: async ({created, eff}) => {
            await runPostCreateDocumentLink(created.id, eff);
            queryClient.invalidateQueries({queryKey: ["/api/knowledgebase"]});
            queryClient.invalidateQueries({queryKey: ["knowledgebase-summary"]});
            toast({
                title: "Document Created",
                description: "Your document has been created successfully",
            });
            resetForm();
        },
        onError: () => {
            toast({
                title: "Creation Failed",
                description: "There was an error creating your document",
                variant: "destructive",
            });
        },
    });

    const updateDocumentMutation = useMutation({
        mutationFn: async (data: typeof formData & { id: string }) => {
            const docData = {
                title: data.title,
                type: data.type,
                content: data.content,
                spaceId: data.spaceId || null,
                topicId: data.topicId || null,
                tagIds: data.tagIds.length > 0 ? data.tagIds : [],
            };

            return await knowledgeBaseService.updateKnowledgeBaseItem(data.id, docData, getSpaceContextForSharedEdit());
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["/api/knowledgebase"]});
            queryClient.invalidateQueries({queryKey: ["knowledgebase-summary"]});
            toast({
                title: "Document Updated",
                description: "Your document has been updated successfully",
            });
            resetForm();
        },
        onError: () => {
            toast({
                title: "Update Failed",
                description: "There was an error updating your document",
                variant: "destructive",
            });
        },
    });

    const handleSaveDocument = useCallback(async () => {
        if (!formData.title.trim()) {
            toast({
                title: "Title Required",
                description: "Please enter a title for your document",
                variant: "destructive",
            });
            return;
        }

        if (isLoadingDocumentRef.current) {
            toast({
                title: "Loading document",
                description: "Please wait until the document finishes loading before saving.",
                duration: 3000,
            });
            return;
        }

        // Prevent duplicate creation: if document already exists (from auto-create), just update it
        const currentEditingId = editingItemId || editingItemIdRef.current;

        if (currentEditingId && editingItemKind !== "reference") {
            // Document already exists, just update it
            autoSaveGenerationRef.current += 1;
            isManualSavingRef.current = true;
            setAutoSaveStatus("saving");

            try {
                const updateData = buildDocumentUpdateData({
                    title: formData.title,
                    type: formData.type,
                    content: formData.content,
                    tagIds: formData.tagIds,
                });
                const updated = await knowledgeBaseService.updateKnowledgeBaseItem(
                    currentEditingId,
                    updateData,
                    getSpaceContextForSharedEdit()
                );
                // Update last saved state to prevent duplicate saves
                lastSavedStateRef.current = {
                    title: formData.title,
                    type: formData.type,
                    content: formData.content,
                    spaceId: formData.spaceId,
                    topicId: formData.topicId,
                    tagIds: [...formData.tagIds],
                };
                if (linksDirtyRef.current) {
                    linksDirtyRef.current = false;
                }
                if (updated) {
                    patchKnowledgeBaseItemInCaches(queryClient, currentEditingId, {
                        title: updated.title,
                        type: updated.type,
                        content: updated.content,
                        tagIds: updated.tagIds,
                        updatedAt: updated.updatedAt,
                    });
                }
                toast({title: "Document Updated", description: "Your document has been updated successfully"});
                setAutoSaveStatus("saved");
                setTimeout(() => setAutoSaveStatus("idle"), 2000);
            } catch (error) {
                console.error("Manual save failed:", error);
                toast({
                    title: "Update Failed",
                    description: "There was an error updating your document",
                    variant: "destructive",
                });
                setAutoSaveStatus("idle");
            } finally {
                setTimeout(() => {
                    isManualSavingRef.current = false;
                }, 100);
            }
            return;
        }

        // Set flag to prevent auto-save during manual save
        autoSaveGenerationRef.current += 1;
        isManualSavingRef.current = true;
        setAutoSaveStatus("saving");

        try {
            if (editingItemKind === "reference" && editingItemId) {
                await sourcesService.updateSource(editingItemId, {
                    title: formData.title,
                    type: formData.type,
                    content: formData.content,
                    url: formData.url || undefined,
                } as any);
                queryClient.invalidateQueries({queryKey: ["/api/sources"]});
                queryClient.invalidateQueries({queryKey: ["source", editingItemId]});
                // Update last saved state to prevent duplicate saves
                lastSavedStateRef.current = {
                    title: formData.title,
                    type: formData.type,
                    content: formData.content,
                    spaceId: "",
                    topicId: "",
                    tagIds: [],
                    url: formData.url,
                };
                toast({title: "Reference Updated", description: "Your reference has been updated."});
                setAutoSaveStatus("saved");
                setTimeout(() => setAutoSaveStatus("idle"), 2000);
            } else if (!currentEditingId) {
                if (selectedDocumentId) {
                    toast({
                        title: "Loading document",
                        description: "Please wait until the document finishes loading before saving.",
                        duration: 3000,
                    });
                    setAutoSaveStatus("idle");
                    return;
                }

                // Create new document only if it doesn't exist yet
                // Check if auto-create is in progress to avoid race condition
                if (isAutoCreatingRef.current) {
                    // Wait a bit for auto-create to complete (max 2 seconds)
                    let waited = 0;
                    while (isAutoCreatingRef.current && waited < 2000) {
                        await new Promise(resolve => setTimeout(resolve, 100));
                        waited += 100;
                    }
                    // Check current editingItemId via ref (avoids stale closure)
                    const currentEditingId = editingItemIdRef.current;
                    if (currentEditingId) {
                        // Document was created by auto-create, just update it
                        const updateData = buildDocumentUpdateData({
                            title: formData.title,
                            type: formData.type,
                            content: formData.content,
                            tagIds: formData.tagIds,
                        });
                        const updatedAfterWait = await knowledgeBaseService.updateKnowledgeBaseItem(
                            currentEditingId,
                            updateData,
                            getSpaceContextForSharedEdit()
                        );
                        lastSavedStateRef.current = {
                            title: formData.title,
                            type: formData.type,
                            content: formData.content,
                            spaceId: formData.spaceId,
                            topicId: formData.topicId,
                            tagIds: [...formData.tagIds],
                        };
                        if (linksDirtyRef.current) {
                            linksDirtyRef.current = false;
                        }
                        if (updatedAfterWait) {
                            patchKnowledgeBaseItemInCaches(queryClient, currentEditingId, {
                                title: updatedAfterWait.title,
                                type: updatedAfterWait.type,
                                content: updatedAfterWait.content,
                                tagIds: updatedAfterWait.tagIds,
                                updatedAt: updatedAfterWait.updatedAt,
                            });
                        }
                        toast({title: "Document Updated", description: "Your document has been updated successfully"});
                        setAutoSaveStatus("saved");
                        setTimeout(() => setAutoSaveStatus("idle"), 2000);
                        return;
                    }
                }

                // Create new document (only if it doesn't exist)
                const effCreate = resolveEffectiveCreateIds(formData.spaceId, formData.topicId);
                const created = await knowledgeBaseService.createKnowledgeBaseItem({
                    title: formData.title,
                    type: formData.type,
                    content: formData.content,
                    spaceId: effCreate.spaceId || null,
                    topicId: effCreate.topicId || null,
                    tagIds: formData.tagIds.length > 0 ? formData.tagIds : [],
                }, currentWorkspaceId ?? null);
                // Set editing ID and last saved state to prevent duplicate creates
                editingItemIdRef.current = created.id;
                setEditingItemId(created.id);
                setEditingItemKind("note");
                lastSavedStateRef.current = {
                    title: formData.title,
                    type: formData.type,
                    content: formData.content,
                    spaceId: formData.spaceId,
                    topicId: formData.topicId,
                    tagIds: [...formData.tagIds],
                };
                persistOpenDocMutation.mutate(created.id);
                await runPostCreateDocumentLink(created.id, effCreate);
                queryClient.invalidateQueries({queryKey: ["/api/knowledgebase"]});
                queryClient.invalidateQueries({queryKey: ["knowledgebase-summary"]});
                toast({title: "Document Created", description: "Your document has been created successfully"});
                setAutoSaveStatus("saved");
                setTimeout(() => setAutoSaveStatus("idle"), 2000);
            }
        } catch (error) {
            console.error("Manual save failed:", error);
            toast({
                title: editingItemId ? "Update Failed" : "Creation Failed",
                description: editingItemId ? "There was an error updating your document" : "There was an error creating your document",
                variant: "destructive",
            });
            setAutoSaveStatus("idle");
        } finally {
            // Clear flag after a short delay to allow state updates to complete
            setTimeout(() => {
                isManualSavingRef.current = false;
            }, 100);
        }
    }, [
        formData,
        editingItemId,
        editingItemKind,
        selectedDocumentId,
        toast,
        persistOpenDocMutation,
        getSpaceContextForSharedEdit,
        buildDocumentUpdateData,
        resolveEffectiveCreateIds,
        runPostCreateDocumentLink,
        currentWorkspaceId,
    ]);

    const handleEditDocument = useCallback(
        async (item: any) => {
            if (item.itemType !== "document") return;

            // Always fetch full document to preserve metadata arrays and tagIds.
            let resolvedItem = item;
            try {
                const fullDoc =
                    (await ensureKnowledgeBaseItem(queryClient, item.id)) ??
                    queryClient.getQueryData(knowledgeBaseItemQueryKey(item.id));
                if (fullDoc) {
                    resolvedItem = fullDoc;
                }
            } catch (error) {
                console.error("Failed to load full document:", error);
            }

            const metadata = resolvedItem.metadata || {};
            const parsedMetadata = typeof metadata === "string" ? (() => {
                try {
                    return JSON.parse(metadata);
                } catch {
                    return {};
                }
            })() : metadata;
            const spaceIds = Array.isArray(parsedMetadata.spaceIds)
                ? parsedMetadata.spaceIds
                : (parsedMetadata.spaceId ? [parsedMetadata.spaceId] : (resolvedItem.spaceId ? [resolvedItem.spaceId] : []));
            const topicIds = Array.isArray(parsedMetadata.topicIds)
                ? parsedMetadata.topicIds
                : (parsedMetadata.topicId ? [parsedMetadata.topicId] : (resolvedItem.topicId ? [resolvedItem.topicId] : []));
            const tagIds = Array.isArray(parsedMetadata.tagIds)
                ? parsedMetadata.tagIds
                : (resolvedItem.tagIds || []);

            setEditingItemKind("note");
            editingItemIdRef.current = resolvedItem.id;
            setEditingItemId(resolvedItem.id);
            const htmlContent = convertToHtml(resolvedItem.content || "");
            setFormData({
                title: resolvedItem.title || "",
                type: resolvedItem.type || "note",
                content: htmlContent,
                spaceId: spaceIds.length > 0 ? spaceIds[0] : "",
                topicId: topicIds.length > 0 ? topicIds[0] : "",
                tagIds,
                syncToGoogleDocs: false,
                url: "",
            });
            editingItemMetadataRef.current = {
                spaceIds: spaceIds.map((id: any) => String(id)),
                topicIds: topicIds.map((id: any) => String(id)),
            };
            persistEditingSpaceContext(spaceIds.map((id: any) => String(id)));
            // Store normalized state for comparison
            lastSavedStateRef.current = {
                title: resolvedItem.title || "",
                type: resolvedItem.type || "note",
                content: htmlContent,
                spaceId: spaceIds.length > 0 ? spaceIds[0] : "",
                topicId: topicIds.length > 0 ? topicIds[0] : "",
                tagIds: [...tagIds],
            };
            onActivateCreateTab?.();

            // Persist this document as the open document
            persistOpenDocMutation.mutate(resolvedItem.id);
            // Reset cancelled flag when user manually selects a document
            hasCancelledEditRef.current = false;
            scheduleEditorBaselineAfterLoad();
        },
        [onActivateCreateTab, scheduleEditorBaselineAfterLoad, persistEditingSpaceContext]
    );

    const handleOpenExistingDocument = useCallback(
        async (doc: any) => {
            await handleEditDocument({...doc, itemType: "document"});
        },
        [handleEditDocument]
    );

    useEffect(() => {
        onAutoSaveStatusChange?.(autoSaveStatus);
    }, [autoSaveStatus, onAutoSaveStatusChange]);

    useEffect(() => {
        onEditingItemIdChange?.(editingItemId);
    }, [editingItemId, onEditingItemIdChange]);

    useEffect(() => {
        onOpenDocumentHandlerChange?.(handleOpenExistingDocument);
    }, [onOpenDocumentHandlerChange, handleOpenExistingDocument]);

    const tabBarContainer =
        tabBarActionsContainerId && typeof document !== "undefined"
            ? document.getElementById(tabBarActionsContainerId)
            : null;

    const tabBarActions =
        tabBarContainer &&
        createPortal(
            <div className="flex items-center gap-2">
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={handleSaveDocument}
                    disabled={autoSaveStatus === "saving" || !formData.title.trim()}
                    title={autoSaveStatus === "saving" ? "Saving..." : editingItemId ? "Update Document" : "Create Document"}
                    data-testid="button-save-sidebar-document"
                >
                    <Save className="h-4 w-4"/>
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => resetForm()}
                    disabled={!editingItemId}
                    title={editingItemId ? "Cancel edit" : "Cancel edit (disabled)"}
                    aria-label="Cancel edit"
                    data-testid="button-reset-edit-mode"
                >
                    <CircleX className="h-4 w-4"/>
                </Button>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-6 w-6"
                                data-testid="button-document-creator-menu">
                            <MoreVertical className="h-4 w-4"/>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem
                            onClick={() => setIsContentFullscreen(!isContentFullscreen)}
                            data-testid="button-toggle-content-fullscreen"
                        >
                            {isContentFullscreen ? <Minimize2 className="h-4 w-4 mr-2"/> :
                                <Maximize2 className="h-4 w-4 mr-2"/>}
                            {isContentFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setLinkDialogOpen(true)} data-testid="button-link-space-topic">
                            <Link2 className="h-4 w-4 mr-2"/>
                            Link Space/Topic
                        </DropdownMenuItem>
                        <DropdownMenuSeparator/>
                        <DropdownMenuItem
                            onClick={() => resetForm()}
                            className="text-destructive focus:text-destructive"
                            data-testid="button-clear-sidebar-document"
                        >
                            <Trash2 className="h-4 w-4 mr-2"/>
                            Delete
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>,
            tabBarContainer
        );

    return (
        <TabsContent value="create" className="flex-1 m-0 mt-0 flex flex-col min-h-0">
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden px-2 py-0 gap-2">
                {/* Single top bar: title (Editing document / Content) + space select + fullscreen */}
                {isContentFullscreen && (
                    <>
                        <div className="shrink-0 flex items-center justify-between py-1.5 px-2 bg-muted/50 rounded-md">
                            <p className="text-sm font-medium">{editingItemId ? (editingItemKind === "reference" ? "Editing reference" : "Editing document") : "Content"}</p>
                            <div className="flex items-center gap-2">
                                {!tabBarActionsContainerId && (
                                    <>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6"
                                            onClick={() => resetForm()}
                                            disabled={!editingItemId}
                                            title={editingItemId ? "Cancel edit" : "Cancel edit (disabled)"}
                                            aria-label="Cancel edit"
                                            data-testid="button-reset-edit-mode-fullscreen"
                                        >
                                            <CircleX className="h-4 w-4"/>
                                        </Button>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon" className="h-6 w-6"
                                                        data-testid="button-document-creator-menu-fullscreen">
                                                    <MoreVertical className="h-4 w-4"/>
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="w-48">
                                                <DropdownMenuItem
                                                    onClick={() => setIsContentFullscreen(!isContentFullscreen)}
                                                    data-testid="button-toggle-content-fullscreen"
                                                >
                                                    <Minimize2 className="h-4 w-4 mr-2"/>
                                                    Exit fullscreen
                                                </DropdownMenuItem>
                                                {editingItemKind !== "reference" && (
                                                    <DropdownMenuItem onClick={() => setLinkDialogOpen(true)}
                                                                      data-testid="button-link-space-topic-fullscreen">
                                                        <Link2 className="h-4 w-4 mr-2"/>
                                                        Link Space/Topic
                                                    </DropdownMenuItem>
                                                )}
                                                <DropdownMenuSeparator/>
                                                <DropdownMenuItem
                                                    onClick={() => resetForm()}
                                                    className="text-destructive focus:text-destructive"
                                                    data-testid="button-clear-sidebar-document-fullscreen"
                                                >
                                                    <Trash2 className="h-4 w-4 mr-2"/>
                                                    Delete
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="flex-1 min-h-[280px] flex flex-col gap-2">
                            <ClipSourceLinkFeedback />
                            <RichTextEditor
                                content={formData.content}
                                onChange={handleContentChange}
                                data-testid="richtext-sidebar-doc-content"
                                minHeight="400px"
                                expandable={true}
                                fillHeight={true}
                                editorRef={editorRef}
                            />
                        </div>
                    </>
                )}

                {/* Other fields - hidden when in fullscreen mode */}
                {!isContentFullscreen && (
                    <>
                        {/* Content bar (title + buttons) - hidden when tab bar has portaled actions to avoid empty bar */}
                        {!tabBarActionsContainerId && (
                            <div
                                className="shrink-0 flex items-center justify-between gap-2 py-1.5 px-2 bg-muted/50 rounded-md">
                                <p className="text-sm font-medium">{editingItemId ? (editingItemKind === "reference" ? "Editing reference" : "Editing document") : "Content"}</p>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6"
                                        onClick={() => resetForm()}
                                        disabled={!editingItemId}
                                        title={editingItemId ? "Cancel edit" : "Cancel edit (disabled)"}
                                        aria-label="Cancel edit"
                                        data-testid="button-reset-edit-mode"
                                    >
                                        <CircleX className="h-4 w-4"/>
                                    </Button>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="icon" className="h-6 w-6"
                                                    data-testid="button-document-creator-menu">
                                                <MoreVertical className="h-4 w-4"/>
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-48">
                                            <DropdownMenuItem
                                                onClick={() => setIsContentFullscreen(!isContentFullscreen)}
                                                data-testid="button-toggle-content-fullscreen"
                                            >
                                                <Maximize2 className="h-4 w-4 mr-2"/>
                                                Enter fullscreen
                                            </DropdownMenuItem>
                                            {editingItemKind !== "reference" && (
                                                <DropdownMenuItem onClick={() => setLinkDialogOpen(true)}
                                                                  data-testid="button-link-space-topic">
                                                    <Link2 className="h-4 w-4 mr-2"/>
                                                    Link Space/Topic
                                                </DropdownMenuItem>
                                            )}
                                            <DropdownMenuSeparator/>
                                            <DropdownMenuItem
                                                onClick={() => resetForm()}
                                                className="text-destructive focus:text-destructive"
                                                data-testid="button-clear-sidebar-document"
                                            >
                                                <Trash2 className="h-4 w-4 mr-2"/>
                                                Delete
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </div>
                        )}

                        <Input
                            id="sidebar-doc-title"
                            placeholder={editingItemKind === "reference" ? "Reference title..." : "Enter document title..."}
                            value={formData.title}
                            onChange={handleTitleChange}
                            data-testid="input-sidebar-doc-title"
                            className="shrink-0 text-lg font-medium border-0 p-0 shadow-none focus-visible:ring-0"
                        />
                        {editingItemKind === "reference" && (
                            <Input
                                id="sidebar-ref-url"
                                placeholder="URL (optional)"
                                value={formData.url}
                                onChange={handleUrlChange}
                                className="shrink-0 text-sm text-muted-foreground"
                                data-testid="input-sidebar-ref-url"
                            />
                        )}

                        <div className="flex-1 min-h-[280px] flex flex-col gap-2">
                            <ClipSourceLinkFeedback />
                            <RichTextEditor
                                content={formData.content}
                                onChange={handleContentChange}
                                data-testid="richtext-sidebar-doc-content"
                                expandable={true}
                                fillHeight={true}
                                editorRef={editorRef}
                            />
                        </div>

                        {tags.length > 0 && editingItemKind !== "reference" && (
                            <div className="shrink-0 flex flex-wrap items-center gap-x-2 gap-y-1">
                                <Label className="text-sm font-medium flex items-center gap-1.5 py-0.5">
                                    <Tag className="h-3.5 w-3.5"/>
                                    Tags
                                </Label>
                                {tags.map((tag) => (
                                    <Badge
                                        key={tag.id}
                                        variant={formData.tagIds.includes(tag.id) ? "default" : "outline"}
                                        className="cursor-pointer text-xs"
                                        style={formData.tagIds.includes(tag.id) ? {backgroundColor: tag.color || undefined} : undefined}
                                        onClick={() => handleTagToggle(tag.id)}
                                        data-testid={`tag-toggle-${tag.id}`}
                                    >
                                        {formData.tagIds.includes(tag.id) && <X className="h-2.5 w-2.5 mr-1"/>}
                                        {tag.name}
                                    </Badge>
                                ))}
                                {formData.tagIds.length > 0 && (
                                    <span className="text-xs text-muted-foreground">
                    ({formData.tagIds.length} selected)
                  </span>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
            {tabBarActions}
            {/* Link Space/Topic popup: selection is committed to formData on each change and when Done is clicked. spaceId/topicId are used when saving (create/update document). */}
            <ThreadLinkingDialog
                open={linkDialogOpen}
                onOpenChange={setLinkDialogOpen}
                commitSelectionOnDone
                title="Link Space/Topic"
                description="Connect this document to spaces, topics, and tags for better organization"
                linkedSpaceIds={editingItemMetadataRef.current?.spaceIds || (formData.spaceId ? [formData.spaceId] : [])}
                topicId={null}
                topicIds={editingItemMetadataRef.current?.topicIds || (formData.topicId ? [formData.topicId] : [])}
                allowMultipleTopics={true}
                tags={formData.tagIds.map((id) => tags.find((t) => t.id === id)?.name).filter(Boolean) as string[]}
                onUpdateSpaces={(spaceIds) => {
                    markUserEdited();
                    linksDirtyRef.current = true;
                    // Update formData and preserve spaceIds array in metadata ref
                    setFormData((prev) => ({...prev, spaceId: spaceIds[0] || ""}));
                    editingItemMetadataRef.current = {
                        ...(editingItemMetadataRef.current || {spaceIds: [], topicIds: []}),
                        spaceIds: spaceIds,
                    };
                }}
                onUpdateTopics={(topicIds) => {
                    markUserEdited();
                    linksDirtyRef.current = true;
                    // Update formData and preserve topicIds array in metadata ref
                    setFormData((prev) => ({...prev, topicId: topicIds[0] || ""}));
                    editingItemMetadataRef.current = {
                        ...(editingItemMetadataRef.current || {spaceIds: [], topicIds: []}),
                        topicIds: topicIds,
                    };
                }}
                onUpdateTopic={(topicId) => {
                    markUserEdited();
                    linksDirtyRef.current = true;
                    // Single topic update (backward compatibility)
                    const topicIds = topicId ? [topicId] : [];
                    setFormData((prev) => ({...prev, topicId: topicId || ""}));
                    editingItemMetadataRef.current = {
                        ...(editingItemMetadataRef.current || {spaceIds: [], topicIds: []}),
                        topicIds: topicIds,
                    };
                }}
                onUpdateTags={async (tagNames) => {
                    markUserEdited();
                    const cached =
                        queryClient.getQueryData<TagType[]>(["/api/tags", user?.id]) ?? tags;
                    const tagIds = await resolveTagNamesToIds(tagNames, cached);
                    setFormData((prev) => ({
                        ...prev,
                        tagIds,
                    }));
                }}
            />
        </TabsContent>
    );
});
